import { Component, OnDestroy, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { NbToastrService } from '@nebular/theme';
import {
  AlTableColumn,
  AlTableGlobalAction,
} from '../../../components/features/table/table.component';
import {
  AuditAction,
  AuditEvent,
  AuditScope,
  AuditService,
} from '../../../services/audit.service';

/**
 * Change log: who created, changed or deleted which agent, knowledge-base
 * item or creator draft.
 *
 * Paging is keyset-based ("load more" with the last id) rather than a page
 * number — the log grows at the head while it is being read, and an OFFSET
 * would quietly repeat or skip rows.
 */
@Component({
  selector: 'app-admin-audit',
  templateUrl: './audit.component.html',
  styleUrl: './audit.component.scss',
  providers: [DatePipe],
})
export class AuditComponent implements OnInit, OnDestroy {
  scope: AuditScope | null = null;
  action: AuditAction | null = null;
  days = 30;

  search = '';
  data: AuditEvent[] = [];
  total = 0;
  page = 0;
  pageSize = 50;
  /** Frozen head of the log for this paging session — see the endpoint. */
  anchorId: number | null = null;
  loading = false;

  readonly pageSizes = [25, 50, 100];
  private readonly searchInput$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  readonly scopes: AuditScope[] = [
    'agent',
    'kb',
    'creator',
    'permission',
    'module_link',
    'security',
  ];
  readonly actions: AuditAction[] = ['create', 'update', 'delete'];
  readonly dayOptions = [7, 30, 90, 365];

  columns: AlTableColumn[] = [];
  globalActions: AlTableGlobalAction[] = [];

  constructor(
    private auditService: AuditService,
    private translate: TranslateService,
    private toastr: NbToastrService,
    private datePipe: DatePipe
  ) {}

  ngOnInit(): void {
    this.columns = [
      {
        key: 'occurred_at', kind: 'date',
        title: this.t('table.when'),
        width: '150px',
        map: (value: string) =>
          this.datePipe.transform(value, 'dd.MM.yyyy HH:mm') || '',
      },
      {
        key: 'actor_label', kind: 'text',
        title: this.t('table.actor'),
        // NULL actor means the change came from a sync or a background job,
        // not from an unknown person — say so instead of showing a dash.
        map: (value: string | null) => value || this.t('system'),
      },
      {
        key: 'scope',
        title: this.t('table.scope'),
        width: '120px',
        map: (value: AuditScope) => this.t(`scopes.${value}`),
      },
      {
        key: 'action',
        title: this.t('table.action'),
        width: '110px',
        map: (value: AuditAction) => this.t(`actions.${value}`),
      },
      {
        key: 'target_label', kind: 'text',
        title: this.t('table.target'),
        map: (value: string | null, row: AuditEvent) =>
          `${this.t(`targets.${row.target_type}`, row.target_type)}: ${
            value || row.target_id || '—'
          }`,
      },
      {
        key: 'changes', kind: 'text',
        title: this.t('table.changes'),
        map: (value: Record<string, unknown[]> | null) =>
          this.formatChanges(value),
      },
    ];

    this.globalActions = [
      {
        title: this.t('export'),
        icon: 'download-outline',
        action: () => this.exportCsv(),
      },
    ];

    // Typing must not fire a request per keystroke; the log is a server-side
    // search over every row, not a filter on what is already loaded.
    this.searchInput$
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe((value) => {
        this.search = value;
        this.reload();
      });

    this.reload();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearch(value: string): void {
    this.searchInput$.next(value);
  }

  get pageCount(): number {
    return Math.max(1, Math.ceil(this.total / this.pageSize));
  }

  get rangeStart(): number {
    return this.total === 0 ? 0 : this.page * this.pageSize + 1;
  }

  get rangeEnd(): number {
    return Math.min(this.total, (this.page + 1) * this.pageSize);
  }

  goToPage(page: number): void {
    const target = Math.min(Math.max(page, 0), this.pageCount - 1);
    if (target === this.page) return;
    this.page = target;
    this.fetch();
  }

  onPageSize(size: number): void {
    this.pageSize = size;
    this.page = 0;
    this.fetch();
  }

  // Explicit setter + reload instead of `[(selected)]` plus a second
  // `(selectedChange)` listener: two bindings on one output would make the
  // reload depend on which listener Angular registered first.
  onScope(value: AuditScope | null): void {
    this.scope = value;
    this.reload();
  }

  onAction(value: AuditAction | null): void {
    this.action = value;
    this.reload();
  }

  onDays(value: number): void {
    this.days = value;
    this.reload();
  }

  /** Filters changed: drop the frozen window and start over at page 1. */
  reload(): void {
    this.anchorId = null;
    this.page = 0;
    this.fetch();
  }

  private fetch(): void {
    this.loading = true;
    this.auditService
      .list({
        q: this.search,
        scope: this.scope,
        action: this.action,
        days: this.days,
        limit: this.pageSize,
        offset: this.page * this.pageSize,
        anchor_id: this.anchorId,
      })
      .subscribe({
        next: (page) => {
          this.data = page.events;
          this.total = page.total;
          // Keep the window the server froze, so later pages line up.
          this.anchorId = page.anchor_id;
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this.toastr.danger(this.t('loadFailed'), this.t('title'));
        },
      });
  }

  exportCsv(): void {
    this.auditService
      .download({
        q: this.search,
        scope: this.scope,
        action: this.action,
        days: this.days,
      })
      .subscribe({
        next: (blob) => {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `audit-${new Date().toISOString().slice(0, 10)}.csv`;
          link.click();
          URL.revokeObjectURL(url);
        },
        error: () =>
          this.toastr.danger(this.t('exportFailed'), this.t('title')),
      });
  }

  /** `{ name: ["alt", "neu"] }` → `name: alt → neu`. Values are already
   *  truncated and secret-masked by the backend. */
  private formatChanges(changes: Record<string, unknown[]> | null): string {
    if (!changes) return '';
    return Object.entries(changes)
      .map(([field, pair]) => {
        const [before, after] = Array.isArray(pair) ? pair : [undefined, pair];
        return `${field}: ${this.short(before)} → ${this.short(after)}`;
      })
      .join('\n');
  }

  private short(value: unknown): string {
    if (value === null || value === undefined || value === '') return '–';
    return String(value);
  }

  private t(key: string, fallback?: string): string {
    const full = `pages.admin.audit.${key}`;
    const translated = this.translate.instant(full);
    return translated === full ? fallback ?? key : translated;
  }
}
